import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/app/store';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { getDisplayTitle, isPlanVisibleToViewer } from '@/domain/plans';
import { partnerOf } from '@/domain/helpers';
import type { Plan, PlanStatus, Profile } from '@/domain/types';
import { PlanChip } from '@/features/plans/PlanChip';
import styles from './SearchView.module.css';

function statusLabel(status: PlanStatus, t: (key: string) => string): string {
  switch (status) {
    case 'active':
      return t('app.filters.active');
    case 'completed':
      return t('app.filters.completed');
    case 'cancelled':
      return t('plan.statusCancelled');
  }
}

function matchesSearchQuery(
  plan: Plan,
  query: string,
  profiles: Profile[],
  viewerId: string,
  t: (key: string) => string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return false;

  const owner = profiles.find((p) => p.id === plan.ownerId);
  const ownerName = owner?.displayName ?? '';
  const visibilityLabel =
    plan.visibility === 'shared' ? t('plan.shared') : t('plan.onlyMe');

  const hay = [
    getDisplayTitle(plan, viewerId, t('availability.busy')),
    plan.description,
    ownerName,
    statusLabel(plan.status, t),
    visibilityLabel,
  ]
    .join(' ')
    .toLowerCase();

  return hay.includes(q);
}

export function SearchView() {
  const { t } = useTranslation();
  const session = useAppStore((s) => s.session);
  const plans = useAppStore((s) => s.plans);
  const profiles = useAppStore((s) => s.profiles);
  const query = useAppStore((s) => s.filters.query);
  const setFilters = useAppStore((s) => s.setFilters);

  const viewerId = session?.user.id ?? '';
  const partner = session ? partnerOf(profiles, session.user.id) : null;

  const results = useMemo(() => {
    if (!viewerId || !query.trim()) return [];
    return plans
      .filter(
        (plan) =>
          !plan.deletedAt &&
          isPlanVisibleToViewer(plan, viewerId) &&
          matchesSearchQuery(plan, query, profiles, viewerId, t),
      )
      .sort((a, b) => {
        const dateCmp = (a.planDate ?? '').localeCompare(b.planDate ?? '');
        if (dateCmp !== 0) return dateCmp;
        return a.title.localeCompare(b.title);
      });
  }, [plans, profiles, query, viewerId, t]);

  if (!session) return null;

  return (
    <div className={styles.view}>
      <h1 className={styles.title}>{t('nav.search')}</h1>
      <Input
        value={query}
        onChange={(e) => setFilters({ query: e.target.value })}
        placeholder={t('app.searchPlaceholder')}
        aria-label={t('common.search')}
        className={styles.input}
      />

      {query.trim() === '' ? (
        <EmptyState
          title={t('common.search')}
          description={t('app.searchPlaceholder')}
        />
      ) : results.length === 0 ? (
        <EmptyState title={t('common.noResults')} />
      ) : (
        <ul className={styles.list}>
          {results.map((plan) => {
            const owner =
              plan.ownerId === viewerId
                ? t('app.filters.mine')
                : (partner?.displayName ?? t('app.filters.partner'));
            const meta = [
              plan.planDate ?? t('app.noDate'),
              owner,
              statusLabel(plan.status, t),
              plan.visibility === 'shared' ? t('plan.shared') : t('plan.onlyMe'),
            ].join(' · ');

            return (
              <li key={plan.id} className={styles.item}>
                <PlanChip plan={plan} compact />
                <span className={styles.meta}>{meta}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
